import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Phone } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import { AuthShell } from "../components/AuthShell";
import { PhoneVerifyModal } from "../components/PhoneVerifyModal";
import { Button } from "../components/Page";
import { EASE } from "../components/motion";
import { Input } from "@/components/ui/input";

/**
 * Sign in.
 *
 * The field is `identifier`, not `email` — the API accepts a phone number or an
 * email and treats them identically, and it deliberately answers the same
 * "Invalid credentials" for an unknown account and a wrong password so the form
 * cannot be used to enumerate registered sellers.
 *
 * The inputs are antd `Input`s (themed from `main.tsx`, so they are the same
 * 38px control as `.field` elsewhere) with a leading Lucide icon, and the
 * password has a visibility toggle — a dealer typing a long password on a phone
 * should not have to guess whether they got it right.
 */
export default function LoginPage() {
  const { signIn, signOut } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the API refuses the sign-in with PHONE_UNVERIFIED. Holds the
  // number the code went to, so the modal never asks for it again.
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const who = identifier.trim();
    setBusy(true);
    setError(null);
    try {
      await signIn(who, password);
    } catch (err) {
      if (err instanceof ApiError && err.isPhoneUnverified) {
        // The password was correct — the API only reaches this check after
        // verifying it — so it is held in state and replayed automatically once
        // the number is confirmed. Clearing it here would leave nothing for the
        // retry, forcing the user to type it a third time.
        setPendingPhone(who);
      } else {
        setError(err instanceof ApiError ? err.message : "Could not sign in. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Sign in"
      lede="Manage your listings, inquiries and showrooms."
      footer={
        <>
          New to BikerHat?{" "}
          <Link to="/register" className="font-medium text-primary underline">
            Create a partner account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5">
        {/* A lock mark above the fields, so the block reads as "secure sign in"
            before a single word is read. */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-wash"
        >
          <Lock className="h-5 w-5 text-primary" aria-hidden />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.08, ease: EASE }}
        >
          <label htmlFor="identifier" className="label">
            Phone or email
          </label>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" aria-hidden />
            <Input
              id="identifier"
              name="identifier"
              autoComplete="username"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="01712345678 or you@example.com"
              className="pl-9"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.16, ease: EASE }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="password" className="label">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
              Forgot password
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-ink-faint transition-colors hover:text-ink"
            >
              {showPassword ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
            </button>
          </div>
        </motion.div>

        {error ? (
          <motion.p
            role="alert"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-r border-l-[3px] border-danger bg-danger-wash px-3.5 py-2.5 text-sm text-danger"
          >
            {error}
          </motion.p>
        ) : null}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.24, ease: EASE }}
        >
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </motion.div>
      </form>

      {/*
        Renders over the page. Not dismissible — sign-in is not finished until
        the number is confirmed, so there is nothing to return to.
      */}
      {pendingPhone ? (
        <PhoneVerifyModal
          identifier={pendingPhone}
          // Retry the sign-in the user already started, with the password still
          // in hand, rather than making them type it a third time.
          onVerified={() => {
            setPendingPhone(null);
            void signIn(pendingPhone, password).catch(() => {
              setError("Your phone is confirmed, but signing in failed. Please try again.");
            });
          }}
          onSignOut={() => {
            setPendingPhone(null);
            setIdentifier("");
            void signOut();
          }}
        />
      ) : null}
    </AuthShell>
  );
}
